import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import { Link } from "react-router-dom";
import { withTranslation } from "react-i18next";
import { Formik, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Button } from "semantic-ui-react";
import { GoogleLogin } from "@react-oauth/google";
import FacebookLogin from "@greatsumini/react-facebook-login";
import {
  onLoginUser as onLoginUserAction,
  onRegister as onRegisterAction,
  onGoogleAuth as onGoogleAuthAction,
  onFacebookAuth as onFacebookAuthAction,
} from "../accountActions";
import LanguageSelector from "../../../common/header/LanguageSelector";
import RevalidateOnLanguageChange from "../../../common/RevalidateOnLanguageChange";
import { normalizeAppLanguageKey } from "../../../../utils/appLanguages";
import "../../../common/layout/AuthLayout.css";
import "./authPage.css";

const FACEBOOK_APP_ID = "796374696548017";

/** i18n key (zh_cn, pt_br, rus, ...) -> Google Identity locale (zh-CN, pt-BR, ru, ...). */
const toGoogleLocale = (language) => {
  const key = normalizeAppLanguageKey(language);
  if (key === "rus") return "ru";
  return key.replace("_", "-").replace(/-(\w+)$/, (_, region) => `-${region.toUpperCase()}`);
};

const SocialButtons = ({ onGoogleSuccess, onFacebookSuccess, facebookLabel, googleLocale, disabled }) => (
  <div className={`auth-modern-social-inline${disabled ? " is-disabled" : ""}`}>
    <div className="auth-modern-google">
      <GoogleLogin
        key={googleLocale}
        locale={googleLocale}
        onSuccess={onGoogleSuccess}
        logo_alignment="center"
        text="continue_with"
        width="359px"
        onError={() => {}}
      />
    </div>
    <FacebookLogin
      appId={FACEBOOK_APP_ID}
      render={(renderProps) => (
        <button
          type="button"
          className="auth-modern-facebook-btn"
          onClick={renderProps.onClick}
          disabled={disabled}
        >
          <i className="fab fa-facebook-f" />
          {facebookLabel}
        </button>
      )}
      onSuccess={onFacebookSuccess}
      onFail={() => {}}
      onProfileSuccess={() => {}}
    />
  </div>
);

const AuthPage = ({
  history,
  location,
  t,
  i18n,
  isLoading,
  loginAction,
  registerAction,
  googleAction,
  facebookAction,
}) => {
  const isSignUpPath = location.pathname === "/register";
  const [isSignUpMode, setIsSignUpMode] = useState(isSignUpPath);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const routeSyncRef = useRef(null);
  const googleLocale = toGoogleLocale(i18n.language);

  useEffect(() => {
    setIsSignUpMode(isSignUpPath);
  }, [isSignUpPath]);

  useEffect(() => {
    return () => {
      if (routeSyncRef.current) {
        clearTimeout(routeSyncRef.current);
      }
    };
  }, []);

  const switchMode = (nextSignUpMode) => {
    setIsSignUpMode(nextSignUpMode);
    if (routeSyncRef.current) {
      clearTimeout(routeSyncRef.current);
    }
    routeSyncRef.current = setTimeout(() => {
      history.push(nextSignUpMode ? "/register" : "/login");
    }, 360);
  };

  const loginValidation = useMemo(
    () =>
      Yup.object().shape({
        email: Yup.string()
          .transform((value) => (typeof value === "string" ? value.trim() : value))
          .email(t("app.auth.validationInvalidEmail"))
          .max(100, t("app.auth.validationMaxChars"))
          .required(t("app.auth.validationEmailRequired")),
        password: Yup.string()
          .min(6, t("app.auth.validationPasswordMin"))
          .max(100, t("app.auth.validationMaxChars"))
          .required(t("app.auth.validationPasswordRequired")),
      }),
    [t]
  );

  const registerValidation = useMemo(
    () =>
      Yup.object().shape({
        ownerName: Yup.string()
          .min(3, t("app.auth.validationOwnerNameMin"))
          .max(200, t("app.auth.validationMaxChars200"))
          .required(t("app.auth.validationFullNameRequired")),
        companyName: Yup.string()
          .min(4, t("app.auth.validationCompanyNameMin"))
          .max(200, t("app.auth.validationMaxChars200"))
          .required(t("app.auth.validationCompanyNameRequired")),
        email: Yup.string()
          .email(t("app.auth.validationInvalidEmail"))
          .max(100, t("app.auth.validationMaxChars"))
          .required(t("app.auth.validationEmailRequired")),
        password: Yup.string()
          .min(6, t("app.auth.validationPasswordMin"))
          .max(100, t("app.auth.validationMaxChars"))
          .required(t("app.auth.validationPasswordRequired")),
        confirmPassword: Yup.string()
          .oneOf([Yup.ref("password"), null], t("app.auth.validationPasswordsMatch"))
          .max(100, t("app.auth.validationMaxChars"))
          .required(t("app.auth.validationConfirmPasswordRequired")),
      }),
    [t]
  );

  const onLoginSubmit = (values) => {
    loginAction({
      email: values.email,
      password: values.password,
      navigation: history,
    });
  };

  const onRegisterSubmit = (values) => {
    registerAction({
      email: values.email,
      password: values.password,
      navigation: history,
      ownerName: values.ownerName,
      companyName: values.companyName,
      registrationNumber: values.registrationNumber,
      phoneNumber: values.phoneNumber,
      address: values.address,
    });
  };

  const onGoogleSuccess = (credentialResponse) => {
    googleAction({
      credential: credentialResponse.credential,
      clientId: credentialResponse.clientId,
      navigation: history,
    });
  };

  const onFacebookSuccess = (response) => {
    if (!response?.accessToken || !(response.userID || response.id)) return;
    facebookAction({
      accessToken: response.accessToken,
      userID: response.userID || response.id,
      navigation: history,
    });
  };

  return (
    <div className="professional-gradient-bg auth-layout-container auth-modern-root">
      <div className="auth-modern-bg-bubble auth-modern-bg-bubble--one" />
      <div className="auth-modern-bg-bubble auth-modern-bg-bubble--two" />
      <div className="auth-modern-ground" />
      <div className="auth-language-corner">
        <LanguageSelector guest />
      </div>

      <div className={`auth-modern-shell ${isSignUpMode ? "sign-up-active" : ""}`}>
        <div className="auth-modern-panel auth-modern-panel--form auth-modern-panel--signin">
          <div className="auth-modern-panel-content">
            <div className="auth-modern-logo-wrap">
              <img src="/assets/img/logo2.png" alt={t("app.auth.logoAlt")} className="auth-modern-logo" />
            </div>
            <h2>{t("app.auth.welcomeBack")}</h2>
            <p>{t("app.auth.signInToAccount")}</p>

            <Formik
              initialValues={{ email: "", password: "" }}
              validationSchema={loginValidation}
              onSubmit={onLoginSubmit}
            >
              {({ handleSubmit }) => (
                <form className="auth-modern-form" onSubmit={handleSubmit} autoComplete="off">
                  <RevalidateOnLanguageChange language={i18n.language} />
                  <div className="auth-modern-field">
                    <label>{t("app.auth.emailAddress")}</label>
                    <Field name="email" type="text" placeholder={t("app.auth.emailAddress")} />
                    <ErrorMessage name="email" component="div" className="auth-modern-error" />
                  </div>

                  <div className="auth-modern-field">
                    <label>{t("app.auth.password")}</label>
                    <div className="auth-modern-password-wrap">
                      <Field
                        name="password"
                        type={showLoginPassword ? "text" : "password"}
                        placeholder={t("app.auth.passwordPlaceholder")}
                      />
                      <button
                        type="button"
                        className="auth-modern-eye"
                        onClick={() => setShowLoginPassword((prev) => !prev)}
                      >
                        <i className={`bi ${showLoginPassword ? "bi-eye-slash" : "bi-eye"}`} />
                      </button>
                    </div>
                    <ErrorMessage name="password" component="div" className="auth-modern-error" />
                  </div>

                  <Link to="/forgot-password" className="auth-modern-forgot">
                    {t("app.auth.forgotPassword")}
                  </Link>

                  <Button loading={isLoading} type="submit" fluid className="auth-modern-submit-btn">
                    {t("app.auth.login")}
                  </Button>
                </form>
              )}
            </Formik>
            <SocialButtons
              onGoogleSuccess={onGoogleSuccess}
              onFacebookSuccess={onFacebookSuccess}
              facebookLabel={t("app.auth.continueWithFacebook")}
              googleLocale={googleLocale}
              disabled={isLoading}
            />
            <div className="auth-modern-footer-links">
              <a href="https://timeoffledger.com/cookie-settings" target="_blank" rel="noopener noreferrer">
                {t("app.auth.cookieSettings")}
              </a>
              <span>|</span>
              <a href="https://timeoffledger.com/cookie-policy" target="_blank" rel="noopener noreferrer">
                {t("app.auth.cookiePolicy")}
              </a>
            </div>
          </div>
        </div>

        <div className="auth-modern-panel auth-modern-panel--form auth-modern-panel--signup">
          <div className="auth-modern-panel-content">
            <div className="auth-modern-logo-wrap">
              <img src="/assets/img/logo2.png" alt={t("app.auth.logoAlt")} className="auth-modern-logo" />
            </div>
            <h2 className="auth-modern-signup-title">{t("app.auth.createAccountTitle")}</h2>
            <p className="auth-modern-signup-subtitle">{t("app.auth.createAccountSubtitle")}</p>

            <Formik
              initialValues={{
                ownerName: "",
                companyName: "",
                email: "",
                password: "",
                confirmPassword: "",
                registrationNumber: "",
                phoneNumber: "",
                address: "",
              }}
              validationSchema={registerValidation}
              onSubmit={onRegisterSubmit}
            >
              {({ handleSubmit }) => (
                <form className="auth-modern-form" onSubmit={handleSubmit} autoComplete="off">
                  <RevalidateOnLanguageChange language={i18n.language} />
                  <div className="auth-modern-form-row auth-modern-form-row--two">
                    <div className="auth-modern-field">
                      <label>{t("app.auth.fullName")}</label>
                      <Field name="ownerName" type="text" placeholder={t("app.auth.fullName")} />
                      <ErrorMessage name="ownerName" component="div" className="auth-modern-error" />
                    </div>
                    <div className="auth-modern-field">
                      <label>{t("app.auth.companyName")}</label>
                      <Field name="companyName" type="text" placeholder={t("app.auth.companyName")} />
                      <ErrorMessage name="companyName" component="div" className="auth-modern-error" />
                    </div>
                  </div>

                  <div className="auth-modern-field auth-modern-field--full">
                    <label>{t("app.auth.emailAddress")}</label>
                    <Field name="email" type="text" placeholder={t("app.auth.emailAddress")} />
                    <ErrorMessage name="email" component="div" className="auth-modern-error" />
                  </div>

                  <div className="auth-modern-form-row auth-modern-form-row--two">
                    <div className="auth-modern-field">
                      <label>{t("app.auth.password")}</label>
                      <div className="auth-modern-password-wrap">
                        <Field
                          name="password"
                          type={showRegisterPassword ? "text" : "password"}
                          placeholder={t("app.auth.password")}
                        />
                        <button
                          type="button"
                          className="auth-modern-eye"
                          onClick={() => setShowRegisterPassword((prev) => !prev)}
                        >
                          <i className={`bi ${showRegisterPassword ? "bi-eye-slash" : "bi-eye"}`} />
                        </button>
                      </div>
                      <ErrorMessage name="password" component="div" className="auth-modern-error" />
                    </div>
                    <div className="auth-modern-field">
                      <label>{t("app.auth.confirmPassword")}</label>
                      <div className="auth-modern-password-wrap">
                        <Field
                          name="confirmPassword"
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder={t("app.auth.confirmPassword")}
                        />
                        <button
                          type="button"
                          className="auth-modern-eye"
                          onClick={() => setShowConfirmPassword((prev) => !prev)}
                        >
                          <i className={`bi ${showConfirmPassword ? "bi-eye-slash" : "bi-eye"}`} />
                        </button>
                      </div>
                      <ErrorMessage name="confirmPassword" component="div" className="auth-modern-error" />
                    </div>
                  </div>

                  <Button loading={isLoading} type="submit" fluid className="auth-modern-submit-btn">
                    {t("app.auth.register")}
                  </Button>
                </form>
              )}
            </Formik>
            <SocialButtons
              onGoogleSuccess={onGoogleSuccess}
              onFacebookSuccess={onFacebookSuccess}
              facebookLabel={t("app.auth.continueWithFacebook")}
              googleLocale={googleLocale}
              disabled={isLoading}
            />
            <div className="auth-modern-footer-links auth-modern-footer-links--stack">
              <div>
                {t("app.auth.termsAgree")}{" "}
                <a href="https://timeoffledger.com/terms-of-service" target="_blank" rel="noopener noreferrer">
                  {t("app.auth.termsOfService")}
                </a>{" "}
                {t("app.auth.andPrivacy")}{" "}
                <a href="https://timeoffledger.com/privacy-policy" target="_blank" rel="noopener noreferrer">
                  {t("app.auth.privacyPolicy")}
                </a>
                .
              </div>
              <div>{t("app.auth.cancelAnytime")}</div>
              <div className="auth-modern-footer-links">
                <a href="https://timeoffledger.com/cookie-settings" target="_blank" rel="noopener noreferrer">
                  {t("app.auth.cookieSettings")}
                </a>
                <span>|</span>
                <a href="https://timeoffledger.com/cookie-policy" target="_blank" rel="noopener noreferrer">
                  {t("app.auth.cookiePolicy")}
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="auth-modern-slider-shell" aria-hidden="true">
          <div className="auth-modern-slider">
            <div className="auth-modern-slider-panel auth-modern-slider-panel--left">
              <div className="auth-modern-slider-logo-wrap">
                <img src="/assets/img/logo2.png" alt={t("app.auth.logoAlt")} className="auth-modern-slider-logo" />
              </div>
              <span className="auth-modern-slider-kicker">{t("app.auth.sliderSecureAccess")}</span>
              <h2 className="auth-modern-slider-title">{t("app.auth.welcomeBack")}</h2>
              <p className="auth-modern-slider-subtitle">{t("app.auth.sliderSignInSubtitle")}</p>
              <button type="button" className="auth-modern-ghost-btn" onClick={() => switchMode(false)}>
                {t("app.auth.signIn")}
              </button>
            </div>
            <div className="auth-modern-slider-panel auth-modern-slider-panel--right">
              <div className="auth-modern-slider-logo-wrap">
                <img src="/assets/img/logo2.png" alt={t("app.auth.logoAlt")} className="auth-modern-slider-logo" />
              </div>
              <span className="auth-modern-slider-kicker">{t("app.auth.sliderNewWorkspace")}</span>
              <h2 className="auth-modern-slider-title">{t("app.auth.sliderBuildHub")}</h2>
              <p className="auth-modern-slider-subtitle">{t("app.auth.sliderBuildHubSubtitle")}</p>
              <button type="button" className="auth-modern-ghost-btn" onClick={() => switchMode(true)}>
                {t("app.auth.signUp")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = ({ accountReducer }) => ({
  isLoading: accountReducer.isLoading,
});

const actions = {
  loginAction: onLoginUserAction,
  registerAction: onRegisterAction,
  googleAction: onGoogleAuthAction,
  facebookAction: onFacebookAuthAction,
};

export default withTranslation()(withRouter(connect(mapStateToProps, actions)(AuthPage)));
